import type { FC, ReactNode } from "react";
import { useEffect, useState } from "react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useWatch } from "react-hook-form";
import { logError } from "../../api/logError";
import { type Answer, type FollowUp, type QuestionCard } from "../types";

/** A question card with follow-ups is a tree structure.
 * Here helpers to traverse that structure can be found */

const shouldFollowUpBeVisible = (rule: FollowUp["rule"], parentValue: Answer["value"] | undefined): boolean => {
  const operators: Record<FollowUp["rule"]["operator"], () => boolean> = {
    /** Add more operators here */
    eq: () => {
      if (typeof rule.parentValue !== "string") {
        logError("'rule.parentValue' only supports values of type string for operator 'eq'");
        return false;
      }

      if (Array.isArray(parentValue)) {
        return extractValues(parentValue).includes(rule.parentValue);
      }
      return parentValue === rule.parentValue;
    },
    any: () => {
      if (!Array.isArray(rule.parentValue)) {
        logError("'rule.parentValue' only supports values of type string for operator 'any'");
        return false;
      } else {
        const ruleValues = extractValues(rule.parentValue);

        if (Array.isArray(parentValue)) {
          return extractValues(parentValue).some(value => ruleValues.includes(value));
        }
        return ruleValues.some(value => value === parentValue);
      }
    },
    containsPartner: () =>
      Array.isArray(parentValue) &&
      parentValue.some(item => typeof item === "object" && "type" in item && item.type === "partner"),
  };
  return operators[rule.operator]();
};

const extractValues = (value: Extract<Answer["value"], Array<unknown>>): string[] => {
  return value
    .map(value => {
      if (typeof value !== "object") return value;
      if ("value" in value) return value.value;
      if ("url" in value) return value.url;
      if ("type" in value) return value.type;

      logError(`Value ${JSON.stringify(value)} not supported as rule value nor parent value`);
      return undefined;
    })
    .filter(value => value !== undefined);
};

export const TraverseQuestionCardComponentBasedOnRule: FC<{
  rootQuestionCard: QuestionCard;
  render: (questionCard: QuestionCard) => ReactNode;
}> = ({ rootQuestionCard, render }) => {
  const value = useWatch({ name: rootQuestionCard.id });

  const [animationRef] = useAutoAnimate();

  /** We do not want to animate on first render */
  const [isFirstRender, setIsFirstRender] = useState(true);
  useEffect(() => setIsFirstRender(false), []);

  const hasFollowUps = rootQuestionCard.followUps && rootQuestionCard.followUps.length > 0;
  return (
    <div ref={hasFollowUps && !isFirstRender ? animationRef : null}>
      {render(rootQuestionCard)}

      {rootQuestionCard.followUps?.map(
        followUp =>
          shouldFollowUpBeVisible(followUp.rule, value) && (
            <TraverseQuestionCardComponentBasedOnRule
              key={followUp.questionCard.id}
              rootQuestionCard={followUp.questionCard}
              render={render}
            />
          )
      )}
    </div>
  );
};

export const traverseQuestionCard = (
  questionCard: QuestionCard,
  options: { onVisit: (current: QuestionCard) => void }
): void => {
  options.onVisit(questionCard);
  questionCard.followUps?.forEach(followUp => traverseQuestionCard(followUp.questionCard, options));
};
